import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, BlockStack, Text, Card, Button, InlineStack, Box, Divider } from '@shopify/polaris';
import { useSubscriptionStatus } from '../hooks/useSubscription';
import { useTierConfig } from '../providers/TierConfigProvider';
import { PageEntrance } from './ui';
import type { Tier, TierFeatureGates } from '../types';
import './FeatureGatedPage.css';

/**
 * FeatureGatedPage — wraps a page component and shows a rich locked state
 * if the store's tier doesn't have access to the required feature.
 *
 * Shows a dark hero banner describing what the feature does, a benefits list,
 * and an upgrade CTA — so locked pages explain value rather than just blocking.
 *
 * @see documents/billing-plan-design.md — "Navigation Paywall Badges"
 * @see Tier Gate UX rules in copilot-instructions.md
 */

const TIER_ORDER: Record<Tier, number> = {
  FREE: 0,
  STARTER: 1,
  GROWTH: 2,
  SCALE: 3,
};

const TIER_LABELS: Record<Tier, string> = {
  FREE: 'Free',
  STARTER: 'Starter',
  GROWTH: 'Growth',
  SCALE: 'Scale',
};

interface FeatureContent {
  icon: string;
  heroTitle: string;
  heroSubtitle: string;
  benefits: string[];
}

/**
 * Per-feature marketing content for the locked state.
 * Copy sourced from website/index.html features section + feature documentation.
 */
const FEATURE_CONTENT: Partial<Record<keyof TierFeatureGates, FeatureContent>> = {
  benchmarking: {
    icon: '📊',
    heroTitle: '🚀 AI is reshaping how consumers find products',
    heroSubtitle:
      'ChatGPT Shopping, Perplexity, Google AI Overviews — they don\'t browse, they select. See exactly where your store stands against the AI-ready threshold and your category peers.',
    benefits: [
      'Your personalized Benchmark Score measured against the 75-point AI-Ready threshold',
      'Compare your store to industry averages, medians, and top-10% performers in real time',
      'Category-level breakdown — see how your niche stacks up vs. Electronics, Fashion, Beauty, and more',
      'Coverage gap analysis: schema markup, GTIN barcodes, and product attribute completeness',
      'Urgency report showing how many competitors in your category have already hit AI-Ready status',
    ],
  },
  aiFeed: {
    icon: '📡',
    heroTitle: '📡 Make your products visible to every AI engine',
    heroSubtitle:
      'Generate a structured product feed that AI crawlers like ChatGPT, Perplexity, and Google AI Overviews can discover and cite — fully automated.',
    benefits: [
      'Hosted JSON feed at ai.zelium.app/feed/your-store — always live and publicly accessible',
      'Auto-generated llms.txt protocol file tells AI crawlers exactly how to index your catalog',
      'Every product entry includes AI summaries, schema.org markup, identifiers, and Zelium quality scores',
      'Daily auto-regeneration keeps your feed in sync — or trigger a manual refresh any time',
      'Crawler analytics dashboard shows which AI systems are discovering your products and when',
    ],
  },
  webhookSync: {
    icon: '🔄',
    heroTitle: 'Keep your AI scores fresh — automatically',
    heroSubtitle:
      'Every product change in Shopify triggers instant rescoring and feed regeneration. No more running manual scans after every update.',
    benefits: [
      'Webhook-driven rescoring fires within minutes of any product update in Shopify',
      'Automatic feed regeneration ensures AI crawlers always see your latest product data',
      'Debounce protection prevents unnecessary re-runs during bulk edit sessions',
      'Daily reconciliation job catches any webhooks that may have been missed',
      'Live sync dashboard shows processed, pending, and failed events in real time',
    ],
  },
  crawlSimulation: {
    icon: '🔬',
    heroTitle: '🔬 See exactly what AI sees on your product pages',
    heroSubtitle:
      'Simulate how ChatGPT, Perplexity, and Google AI Overviews crawl your product pages — and find out what they\'re missing.',
    benefits: [
      'Side-by-side "What AI sees vs. What AI needs" comparison for any product',
      'Identify missing structured data, schema gaps, and extractability failures',
      'Highlight which product fields AI systems can and cannot read',
      'Actionable fix suggestions ranked by impact on AI discoverability',
      'Re-simulate after applying fixes to verify improvement',
    ],
  },
  bulkOps: {
    icon: '⚡',
    heroTitle: '⚡ Fix your entire catalog in one go',
    heroSubtitle:
      'Apply AI-powered optimisations across hundreds of products at once — without opening a single product page.',
    benefits: [
      'Bulk-apply AI-Boost across your entire catalog or filtered product segments',
      'Priority queue so your most critical products are fixed first',
      'Full job history with per-product results and rollback capability',
      'Scheduled bulk jobs run overnight — wake up to an optimised catalog',
      'Progress dashboard shows completion status, errors, and impact summary',
    ],
  },
};

interface FeatureGatedPageProps {
  /** The feature key to check (must match TierFeatureGates key) */
  feature: keyof TierFeatureGates;
  /** Display name of the feature for the page title */
  featureName: string;
  /** The actual page component to render when access is granted */
  children: React.ReactNode;
  /** Optional analytics source label for paywall tracking */
  source?: string;
}

export function FeatureGatedPage({
  feature,
  featureName,
  children,
}: Readonly<FeatureGatedPageProps>): React.JSX.Element {
  const { data: status, isLoading } = useSubscriptionStatus();
  const { minTierPerFeature } = useTierConfig();
  const navigate = useNavigate();

  const currentTier = status?.tier ?? 'FREE';
  const requiredTier = minTierPerFeature[feature] ?? 'GROWTH';
  const hasAccess = status?.features?.[feature] ?? (TIER_ORDER[currentTier] >= TIER_ORDER[requiredTier]);

  if (isLoading) {
    return <Page title={featureName}><Box minHeight="200px" /></Page>;
  }

  if (!hasAccess) {
    const content = FEATURE_CONTENT[feature];

    return (
      <PageEntrance>
        <Page
          title={featureName}
          backAction={{ content: 'Dashboard', url: '/dashboard' }}
        >
          <BlockStack gap="500">

            {/* ── Dark hero ─────────────────────────────────────────── */}
            <div className="fgp-locked-hero">
              <div className="fgp-locked-hero-content">
                {content && <div className="fgp-locked-icon">{content.icon}</div>}

                <h2 className="fgp-locked-title">
                  {content ? content.heroTitle : featureName}
                </h2>

                <p className="fgp-locked-desc">
                  {content
                    ? content.heroSubtitle
                    : `Upgrade to unlock ${featureName.toLowerCase()} and other premium features.`}
                </p>

                <div className="fgp-tier-badge">
                  🔒 Requires {TIER_LABELS[requiredTier]} plan
                </div>

                <div className="fgp-locked-actions">
                  <button
                    className="z-btn-primary"
                    onClick={() => navigate('/billing')}
                  >
                    Upgrade to {TIER_LABELS[requiredTier]} →
                  </button>
                </div>
              </div>
            </div>

            {/* ── Benefits card ──────────────────────────────────────── */}
            {content && content.benefits.length > 0 && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    What you unlock on {TIER_LABELS[requiredTier]}+
                  </Text>

                  <div className="fgp-benefits">
                    {content.benefits.map((benefit) => (
                      <div key={benefit} className="fgp-benefit-item">
                        <span className="fgp-benefit-check">✓</span>
                        <Text as="p" variant="bodyMd">{benefit}</Text>
                      </div>
                    ))}
                  </div>

                  <Divider />

                  <InlineStack>
                    <Button variant="primary" onClick={() => navigate('/billing')}>
                      Upgrade to {TIER_LABELS[requiredTier]}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}

          </BlockStack>

        </Page>
      </PageEntrance>
    );
  }

  return <>{children}</>;
}
